import React from 'react';
import { 
  Shield, FileText, RefreshCw, Trash2, ExternalLink, Scale
} from 'lucide-react';

/**
 * Legal & Compliance Settings Page
 * Shows links to all legal policy sections
 */
const LegalComplianceSettings = () => {
  const legalSections = [
    {
      id: 'privacy',
      title: 'Privacy Policy',
      description: 'How we collect, process, store, and protect your information',
      icon: Shield,
      color: 'emerald',
      anchor: '#privacy'
    },
    {
      id: 'terms',
      title: 'Terms & Conditions',
      description: 'Rules and guidelines for using Superhost CRM',
      icon: FileText,
      color: 'indigo',
      anchor: '#terms'
    },
    {
      id: 'refund',
      title: 'Refund Policy',
      description: 'Payment terms, refund eligibility, and process',
      icon: RefreshCw,
      color: 'amber',
      anchor: '#refund'
    },
    {
      id: 'deletion',
      title: 'Data Deletion Policy',
      description: 'How to request account and data deletion',
      icon: Trash2,
      color: 'red',
      anchor: '#deletion'
    }
  ];

  const colorClasses = {
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100', hover: 'hover:border-emerald-200' },
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-indigo-100', hover: 'hover:border-indigo-200' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-100', hover: 'hover:border-amber-200' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-red-100', hover: 'hover:border-red-200' }
  };

  const handleViewPolicy = (anchor) => {
    window.open(`/legal${anchor}`, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="legal-compliance-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-xl">
              <Scale className="text-slate-600" size={24} />
            </div>
            Legal & Compliance
          </h1>
          <p className="text-gray-500 mt-1">
            View and access Superhost CRM legal policies and compliance documents
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="text-blue-600 mt-0.5" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Compliance Information</p>
            <p>
              Superhost CRM complies with the Digital Personal Data Protection Act (DPDPA) 2023 India 
              and follows GDPR principles where applicable. All policies are effective from January 1, 2026.
            </p>
          </div>
        </div>
      </div>

      {/* Legal Policy Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {legalSections.map((section) => {
          const Icon = section.icon;
          const colors = colorClasses[section.color];
          
          return (
            <div 
              key={section.id}
              className={`bg-white border ${colors.border} ${colors.hover} rounded-xl p-5 transition-all cursor-pointer group`}
              onClick={() => handleViewPolicy(section.anchor)}
              data-testid={`legal-card-${section.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 ${colors.bg} rounded-xl`}>
                  <Icon className={colors.icon} size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                    {section.title}
                    <ExternalLink size={14} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                  <p className="text-sm text-gray-500">
                    {section.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Links */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-800 mb-4">Quick Links</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleViewPolicy('#privacy')}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all inline-flex items-center gap-2"
            data-testid="view-privacy-policy-btn"
          >
            <Shield size={16} className="text-emerald-600" />
            View Privacy Policy
          </button>
          <button
            onClick={() => handleViewPolicy('#terms')}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all inline-flex items-center gap-2"
            data-testid="view-terms-btn"
          >
            <FileText size={16} className="text-indigo-600" />
            View Terms
          </button>
          <button
            onClick={() => handleViewPolicy('#refund')}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all inline-flex items-center gap-2"
            data-testid="view-refund-policy-btn"
          >
            <RefreshCw size={16} className="text-amber-600" />
            View Refund Policy
          </button>
          <button
            onClick={() => handleViewPolicy('#deletion')}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all inline-flex items-center gap-2"
            data-testid="view-data-deletion-btn"
          >
            <Trash2 size={16} className="text-red-600" />
            View Data Deletion Policy
          </button>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-800 mb-3">Need Help?</h3>
        <p className="text-sm text-gray-600 mb-4">
          For questions about our legal policies or to submit data requests, contact us:
        </p>
        <div className="flex flex-col sm:flex-row gap-4 text-sm">
          <a 
            href="mailto:support@getroombookings.com" 
            className="text-teal-600 hover:text-teal-700 font-medium"
          >
            support@getroombookings.com
          </a>
          <a 
            href="tel:+919310651733" 
            className="text-teal-600 hover:text-teal-700 font-medium"
          >
            +91 9310651733
          </a>
        </div>
      </div>
    </div>
  );
};

export default LegalComplianceSettings;
